import type { ColumnDef } from "@tanstack/react-table";
import { MessageSquareWarning } from "lucide-react";
import { Stack } from "@/core/components/ui/stack";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/core/components/ui/tooltip";
import { Typography } from "@/core/components/ui/typography";
import type { PayrollStatementNotificationRecipient } from "@/payroll/pay-breakdown/payroll-statement-notifications/types";
import { getNotificationReasonLabel } from "@/payroll/pay-breakdown/payroll-statement-notifications/utils";

export const TechnicianCol = (
  /** Technicians with an open PackChat thread, marked so the row reads at a glance. */
  awaitingReplyTechnicianIds?: Set<string>,
): ColumnDef<PayrollStatementNotificationRecipient, unknown> => ({
  id: "technician",
  enableSorting: false,
  header: () => <span>Technician</span>,
  cell: ({ row }) => {
    const recipient = row.original;
    const ineligibleReason = getNotificationReasonLabel(
      recipient.ineligible_reason,
    );
    const awaitingFeedback = Boolean(
      awaitingReplyTechnicianIds?.has(recipient.technician_id),
    );

    return (
      <Stack gap="xs" className="min-w-0">
        <Stack direction="row" align="center" gap="xs" className="min-w-0">
          {awaitingFeedback && (
            <Tooltip>
              <TooltipTrigger>
                <MessageSquareWarning
                  className="size-4 shrink-0 text-gold-70"
                  aria-label="Has an open PackChat thread"
                  role="img"
                />
              </TooltipTrigger>
              <TooltipContent>Open thread. See PackChat.</TooltipContent>
            </Tooltip>
          )}
          <Typography variant="subtitleW3" className="truncate">
            {recipient.name}
          </Typography>
        </Stack>
        {!recipient.eligible && ineligibleReason ? (
          <Typography variant="captionW1" textColor="negative">
            {ineligibleReason}
          </Typography>
        ) : (
          <Typography variant="captionW1" textColor="tertiary">
            {recipient.technician_external_id ?? "Unlinked technician"}
          </Typography>
        )}
      </Stack>
    );
  },
});
